import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/contexts/AuthContext";
import React, { useEffect, useState } from "react";
import { Form, Button, Typography, Flex, message } from "antd";
import FormInput from "../../../shared/components/form/formInput/FormInput";
import hff_donors_logo_dark from "./../../../assets/images/hff_donors_logo_dark.png";
import { AUTH_SERVICE } from "../../../shared/services/auth-service";
import { setupAuthData, removeAuthData } from "../../../shared/utils/auth";
import "./Login.scss";
import { setPageTitle } from "../../../shared/utils/common";
import { LOGIN_URL } from "../../../shared/constants/endpoints";

const { Title } = Typography;

interface LoginFormValues {
  email: string;
  password: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  roles: string[];
  permissions: string[];
}

interface LoginResponse {
  success: boolean;
  status: number;
  data: {
    token: string;
    user: User;
  };
}

interface ApiErrorResponse {
  success: boolean;
  status: number;
  message: string;
}

interface ApiError {
  response?: {
    data: ApiErrorResponse;
  };
  errorFields?: boolean;
}

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { setIsAuth, setUser, setToken, setUserRole, setPermissions } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    try {

      const values: LoginFormValues = await form.validateFields();
      setLoading(true);

      const response = await AUTH_SERVICE.login(LOGIN_URL, values) as LoginResponse;

      if (!response || !response?.success || !response.data) {
        throw new Error("Invalid response format");
      }

      const { token, user } = response?.data;

      if (!token || !user || !user.roles || !user.roles.length) {
        throw new Error("Missing required user data");
      }

      try {
        setupAuthData(token, user);

        setIsAuth(true);
        setUser(user);
        setToken(token);
        setUserRole(user.roles[0]);
        setPermissions(user.permissions || []);

        message.success("Login successful!");
        navigate("/dashboard");
      } catch (stateError) {
        removeAuthData();
        throw new Error("Failed to setup authentication");
      }
    } catch (error: any) {
      setLoading(false);
      
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.errorFields) {
        message.error("Please fill in all required fields.");
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    setPageTitle("Login");
  }, []);

  return (
    <div
      className="login-page"

      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f0f2f5",
      }}
    >
      <Flex
        vertical
        className=" box-shadow-thin"
        style={{
          width: 500,
          padding: 30,
          backgroundColor: "#fff",
          margin: "10px",
        }}
      >
        <Flex justify="center" align="center" vertical>
          <img src={hff_donors_logo_dark} alt="HFF Logo" width={265}/>
          <Title level={3} className="p-10" style={{ textAlign: "center", marginBottom: 20 }}>
            Login
          </Title>
        </Flex>

        <Form onFinish={handleLogin} form={form} name="loginForm" layout="vertical" requiredMark={false}>
          <Flex vertical gap={32}>
            <FormInput
              label="Email"
              name="email"
              type="email"
              className="input-height "
              rules={[
                { required: true, message: "Please input your Email!" },
                { type: "email", message: "Please enter a valid email!" },
              ]}
            />

            <FormInput
              label="Password"
              name="password"
              type="password"
              className="input-height "
              rules={[{ required: true, message: "Please input your password!" }]}
            />

            <Form.Item>
              <Button
                className="input-height"
                type="primary"
                block
                htmlType="submit"
                loading={loading}
              >
                Login
              </Button>
            </Form.Item>
          </Flex>
          <div style={{ textAlign: "center" }}>
            <Link to="/forgot-password">Forgot Password?</Link>
          </div>
        </Form>
      </Flex>
    </div>
  );
};

export default Login;
